import React, { useRef, useState, useEffect, useCallback } from 'react';
import liff from '@line/liff';
import { Html5Qrcode } from 'html5-qrcode';

const LIFF_ID = process.env.REACT_APP_LIFF_ID;
const REGION_ID = 'bts-test-qr-region';

// Strip control characters (NUL, newline, tab, etc.) ที่บางทีปนมาจาก POS/printer
// eslint-disable-next-line no-control-regex
const sanitize = (text) => (text || '').replace(/[\x00-\x1F\x7F]/g, '').trim();

const parseQrParams = (text) => {
  try {
    const url = new URL(sanitize(text));
    return {
      customer: url.searchParams.get('customer'),
      receiptNo: url.searchParams.get('receiptNo'),
      partnerId: url.searchParams.get('partnerId'),
      sale: url.searchParams.get('sale'),
      sale_date: url.searchParams.get('sale_date'),
      sale_store: url.searchParams.get('sale_store'),
    };
  } catch {
    return null;
  }
};

const hasAllFields = (params) => {
  if (!params) return false;
  return ['customer', 'receiptNo', 'partnerId', 'sale_date', 'sale_store', 'sale']
    .every(k => !!params[k]);
};

// =====================================================
// TestScanPage — NEW experimental scanner
//   - Prefer LIFF native (liff.scanCodeV2) — no allow prompt บน Android/iOS
//   - Fallback: html5-qrcode (สำหรับ test ใน browser ปกติ)
//   - ไม่มีปุ่ม torch
//   - หน้านี้แยกจาก ScanPage หลัก (เพื่อ test ก่อนเปลี่ยน production)
// =====================================================
const TestScanPage = () => {
  const [status, setStatus] = useState('init'); // init | scanning | redirecting | denied
  const [error, setError] = useState(null);
  const [usingLiff, setUsingLiff] = useState(false);
  const qrRef = useRef(null);
  const processedRef = useRef(false);
  const startingRef = useRef(false);

  const goBack = () => window.history.back();

  const processText = useCallback((decodedText, stopFn) => {
    if (processedRef.current || !decodedText) return;
    const cleaned = sanitize(decodedText);
    const params = parseQrParams(cleaned);

    if (hasAllFields(params)) {
      processedRef.current = true;
      setStatus('redirecting');
      if (stopFn) stopFn();
      const query = new URLSearchParams(params).toString();
      window.location.href = `https://liff.line.me/${LIFF_ID}/game/bts/receipt?${query}`;
      return;
    }
    try {
      const url = new URL(cleaned);
      if (url.protocol.startsWith('http')) {
        processedRef.current = true;
        setStatus('redirecting');
        if (stopFn) stopFn();
        window.location.href = cleaned;
        return;
      }
    } catch { /* not URL */ }
    setError('QR นี้ไม่ใช่ใบเสร็จ Big C');
    setTimeout(() => setError(null), 3000);
  }, []);

  const stopHtml5 = useCallback(async () => {
    const inst = qrRef.current;
    if (!inst) return;
    try {
      if (inst.isScanning) await inst.stop();
      await inst.clear();
    } catch (_) { /* ignore */ }
  }, []);

  // lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (startingRef.current) return;
    startingRef.current = true;

    const useLiffScanner = liff?.isInClient?.() && typeof liff.scanCodeV2 === 'function';
    setUsingLiff(useLiffScanner);
    setStatus('scanning');

    if (useLiffScanner) {
      liff.scanCodeV2()
        .then(res => {
          const text = res?.value || '';
          if (!text) {
            goBack();
            return;
          }
          processText(text, null);
        })
        .catch(err => {
          console.error('[TestScan] liff.scanCodeV2 error:', err);
          if (/cancel/i.test(String(err?.message || err))) {
            goBack();
            return;
          }
          setError('เปิดกล้อง LINE ไม่สำเร็จ กรุณาลองใหม่');
        });
      return;
    }

    // Fallback: html5-qrcode (browser ปกติ)
    const qr = new Html5Qrcode(REGION_ID, { verbose: false });
    qrRef.current = qr;
    const config = {
      fps: 12,
      qrbox: (vw, vh) => {
        const s = Math.floor(Math.min(vw, vh) * 0.75);
        return { width: s, height: s };
      },
      videoConstraints: {
        facingMode: { ideal: 'environment' },
        advanced: [{ focusMode: 'continuous' }],
      },
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
    };
    qr.start({ facingMode: { ideal: 'environment' } }, config,
      (text) => processText(text, () => stopHtml5()),
      () => { /* per-frame decode error: ignore */ })
      .catch(err => {
        console.error('[TestScan] html5-qrcode start error:', err);
        const msg = String(err?.message || err || '');
        if (/Permission|NotAllowed|denied/i.test(msg)) {
          setStatus('denied');
          setError('กรุณาอนุญาตให้ใช้กล้องในเบราว์เซอร์ แล้วลองใหม่');
        } else {
          setError('เปิดกล้องไม่สำเร็จ: ' + msg);
        }
      });

    return () => { stopHtml5(); };
  }, [processText, stopHtml5]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex', flexDirection: 'column', zIndex: 9999 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', zIndex: 10 }}>
        <button onClick={goBack} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 36, height: 36, color: '#fff', fontSize: 18, cursor: 'pointer' }}>✕</button>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>สแกน QR บิล (Test {usingLiff ? '· LINE' : '· Web'})</span>
        <div style={{ width: 36 }} />
      </div>

      {/* container สำหรับ html5-qrcode fallback */}
      <div id={REGION_ID} style={{ flex: 1, position: 'relative', background: '#000', minHeight: 0 }} />

      {(status === 'init' || status === 'scanning') && !error && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', pointerEvents: 'none', zIndex: 5 }}>
          <div style={{ textAlign: 'center', fontSize: 14, opacity: 0.85 }}>{usingLiff ? 'กำลังเปิดกล้อง LINE...' : 'กำลังเปิดกล้อง...'}</div>
        </div>
      )}

      {status === 'redirecting' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: 'rgba(0,0,0,0.7)', zIndex: 20 }}>กำลัง redirect...</div>
      )}

      {error && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 30, padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, maxWidth: 320, width: '100%', textAlign: 'center' }}>
            <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>⚠️ {status === 'denied' ? 'ไม่ได้รับสิทธิ์ใช้กล้อง' : 'เกิดข้อผิดพลาด'}</p>
            <p style={{ fontSize: 13, color: '#666', margin: '0 0 16px' }}>{error}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button onClick={() => { setError(null); processedRef.current = false; window.location.reload(); }} style={{ background: '#10C851', color: '#fff', border: 'none', borderRadius: 100, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>ลองใหม่</button>
              <button onClick={goBack} style={{ background: '#888', color: '#fff', border: 'none', borderRadius: 100, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>ปิด</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestScanPage;
